// <variant-picker>: live price/availability/image/URL update on option change.
customElements.define(
  "variant-picker",
  class extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(this.querySelector("[data-variant-data]").textContent); }
      catch { this.variants = []; }
      this.idInput = this.querySelector("[data-variant-id]");
      this.priceEl = this.querySelector("[data-price-target]");
      this.compareEl = this.querySelector("[data-compare-target]");
      this.atc = this.querySelector("[data-atc]");
      this.atcLabel = this.querySelector("[data-atc-label]");
      // Shopify's dynamic-checkout button ("Buy it now" / Shop Pay) is not
      // availability-aware, so it has to be hidden alongside a sold-out variant.
      this.dynamicCheckout = this.querySelector("[data-dynamic-checkout]");
      // data-inline: this picker is a CARD in a listing, not the page's own product. Two
      // things must not happen there. Rewriting the address bar would put ?variant= on a
      // collection URL, and every other card on the page would fight over it. And
      // gallery:select is dispatched on `document`, so a recommendation card's swatch
      // would swap the product page's own gallery. An inline picker repaints only itself.
      this.inline = this.hasAttribute("data-inline");
      this.soldOut = this.dataset.soldOut || "Sold out";
      this.addLabel = this.dataset.addLabel || "Add to cart";
      this.unavailable = this.dataset.unavailable || "Unavailable";
      this.addEventListener("change", (e) => { if (e.target.matches("[data-option]")) this.onChange(); });

      // Coming BACK to a listing, the browser restores which radio was checked but fires
      // no change event -- so a card sat there with Red selected and the black photograph
      // still on it, exactly as the server rendered it. Re-derive from the DOM instead of
      // trusting that a change event happened.
      //
      // Twice, because the two restores land at different moments: a normal back-forward
      // navigation re-parses the page and restores form state around load, while a
      // bfcache restore fires pageshow with no parsing at all. Cards only: on the product
      // page this would also re-stamp ?variant= and re-fire gallery:select at load.
      if (this.inline) {
        this.syncFromDom();
        this._onShow = () => this.syncFromDom();
        window.addEventListener("pageshow", this._onShow);
      }
    }

    disconnectedCallback() {
      if (this._onShow) window.removeEventListener("pageshow", this._onShow);
    }

    /** Repaint from whatever is checked right now, if that is not what is on screen. */
    syncFromDom() {
      if (this.variants.length === 0) return;
      this.onChange();
    }
    selected() {
      const groups = {};
      this.querySelectorAll("[data-option]").forEach((el) => {
        if (el.type === "radio" && !el.checked) return;
        groups[el.dataset.optionIndex] = el.value;
      });
      return Object.keys(groups).sort().map((k) => groups[k]);
    }
    onChange() {
      const opts = this.selected();
      const variant = this.variants.find(
        (v) => v.options.length === opts.length && v.options.every((o, i) => o === opts[i])
      );
      this.render(variant);
    }
    render(variant) {
      if (!variant) {
        if (this.atc) this.atc.disabled = true;
        if (this.atcLabel) this.atcLabel.textContent = this.unavailable;
        if (this.dynamicCheckout) this.dynamicCheckout.hidden = true;
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      if (!this.inline) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      }
      if (this.priceEl) this.priceEl.innerHTML = variant.price;
      if (this.compareEl) {
        this.compareEl.innerHTML = variant.compare || "";
        this.compareEl.classList.toggle("hidden", !variant.compare);
      }
      if (this.atc) this.atc.disabled = !variant.available;
      if (this.atcLabel) this.atcLabel.textContent = variant.available ? this.addLabel : this.soldOut;
      if (this.dynamicCheckout) this.dynamicCheckout.hidden = !variant.available;
      const skuEl = this.querySelector("[data-sku]");
      if (skuEl && variant.sku !== undefined) skuEl.textContent = variant.sku;
      if (this.inline) {
        this.swapCardImage(variant.image);
      } else if (variant.media) {
        document.dispatchEvent(new CustomEvent("gallery:select", { detail: { id: variant.media } }));
      }
    }

    // The card's own photo, crossfaded. The new file is decoded BEFORE the src changes:
    // swapping first and fading after shows an empty box for as long as the download
    // takes, which is worse than not animating at all. The fade itself is
    // [data-card-image]'s, declared in _product-card.liquid's stylesheet.
    //
    // image_tag cannot emit a hyphenated data attribute, so the hook sits on the wrapper
    // rather than the <img>.
    swapCardImage(src) {
      const wrap = this.querySelector("[data-card-image]");
      const img = wrap && wrap.querySelector("img");
      if (!img || !src || img.getAttribute("src") === src) return;
      const done = () => wrap.classList.remove("is-swapping");
      const next = new Image();
      next.addEventListener("load", () => {
        // srcset FIRST, and this is the whole reason a correct src swap changed nothing:
        // image_tag renders the card photo with a srcset (…&width=352 352w, …&width=520
        // 520w), and a browser picks its source from srcset, ignoring src. Dropping the
        // candidates costs nothing here -- src is the 520w URL, which is what a card 280px
        // wide already resolved to on a 2x screen.
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        img.src = src;
        done();
      }, { once: true });
      // A variant photo that 404s must not leave the card faded out for good.
      next.addEventListener("error", done, { once: true });
      wrap.classList.add("is-swapping");
      next.src = src;
    }
  }
);
